import React from 'react';

interface PaletteSwitcherProps {
  onPaletteChange: (paletteName: string) => void;
  currentPalette: string;
  isLoading?: boolean;
}

const PaletteSwitcher: React.FC<PaletteSwitcherProps> = ({ 
  onPaletteChange, 
  currentPalette,
  isLoading = false
}) => {
  const palettes = [
    {
      name: 'nature-forest',
      displayName: 'Nature Forest',
      description: 'Deep forest greens with warm earth tones',
      primaryColor: '#1a603d',
      secondaryColor: '#4f8c5e'
    },
    {
      name: 'rich-olive',
      displayName: 'Rich Olive',
      description: 'Rich, earthy olive greens with warm undertones',
      primaryColor: '#496c1e',
      secondaryColor: '#6c8f38'
    },
    {
      name: 'fresh-market',
      displayName: 'Fresh Market',
      description: 'Basil green + warm pumpkin accents',
      primaryColor: '#2E7D32',
      secondaryColor: '#F59E0B'
    },
    {
      name: 'calm-teal',
      displayName: 'Calm Teal',
      description: 'Caring teal with warm sand',
      primaryColor: '#2A9D8F',
      secondaryColor: '#E9C46A'
    },
    {
      name: 'minimal-emerald',
      displayName: 'Minimal Emerald',
      description: 'Clean emerald with neutral gray',
      primaryColor: '#16A34A',
      secondaryColor: '#6B7280'
    }
  ];

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <h3 className="text-lg font-semibold mb-4">Palette Switcher</h3>
      <div className="space-y-3">
        {palettes.map((palette) => (
          <div 
            key={palette.name}
            className={`p-4 border-2 rounded-lg transition-all ${
              isLoading 
                ? 'cursor-not-allowed opacity-50' 
                : 'cursor-pointer'
            } ${
              currentPalette === palette.name 
                ? 'border-primary-500 bg-primary-50' 
                : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
            }`}
            onClick={() => !isLoading && onPaletteChange(palette.name)}
          >
            <div className="flex items-center space-x-3">
              <div className="flex space-x-1">
                <div 
                  className="w-6 h-6 rounded border"
                  style={{ backgroundColor: palette.primaryColor }}
                />
                <div 
                  className="w-6 h-6 rounded border"
                  style={{ backgroundColor: palette.secondaryColor }}
                />
              </div>
              <div className="flex-1">
                <div className="font-medium">{palette.displayName}</div>
                <div className="text-sm text-gray-600">{palette.description}</div>
              </div>
              {currentPalette === palette.name && (
                <div className="text-primary-600 font-medium">
                  {isLoading ? '⏳ Loading...' : '✓ Active'}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <div className="text-sm text-gray-600">
          <strong>Current Palette:</strong> {palettes.find(p => p.name === currentPalette)?.displayName}
        </div>
        <div className="text-xs text-gray-500 mt-1">
          Click a palette above to switch. The change will be applied immediately.
        </div>
      </div>
    </div>
  );
};

export default PaletteSwitcher; 